import { h, ref, VNodeChild } from "vue";
import { useDialog, NButton, DialogReactive } from "naive-ui";

/**
 * 사용자 정의 가능한 액션 버튼을 포함하는 다이얼로그를 표시하기 위한 옵션 인터페이스
 */
interface UseActionDialogOptions {
    title?: string;
    content?: string | (() => VNodeChild);
    showIcon?: boolean;
    maskClosable?: boolean;
    closable?: boolean;
    positiveText?: string;
    negativeText?: string;
    onCancel?: () => void | Promise<void>; // 취소 콜백 (비동기 가능)
    onConfirm: () => Promise<void>; // 확인 콜백 (반드시 비동기)
    positiveButtonProps?: Partial<InstanceType<typeof NButton>["$props"]>;
    negativeButtonProps?: Partial<InstanceType<typeof NButton>["$props"]>;
    confirmButtonLoadingText?: string;
}

/**
 * 재사용 가능한 액션 다이얼로그 컴포저블 함수
 * @returns {object} show - 다이얼로그를 표시하는 함수를 포함하는 객체
 */
export function useActionDialog() {
    const dialogApi = useDialog();

    const show = (options: UseActionDialogOptions) => {
        const {
            title = "작업 확인",
            content = "정말로 이 작업을 수행하시겠습니까?",
            showIcon = false,
            maskClosable = false,
            closable = false,
            positiveText = "확인",
            negativeText = "취소",
            onCancel,
            onConfirm,
            positiveButtonProps = { type: "primary" },
            negativeButtonProps = {},
            confirmButtonLoadingText = "처리 중...",
        } = options;

        const isLoading = ref(false); // 현재 다이얼로그의 확인 버튼 로딩 상태
        let dialogInstance: DialogReactive | null = null;

        const destroyCurrentDialog = () => {
            if (dialogInstance) {
                dialogInstance.destroy();
                dialogInstance = null;
            }
        };

        const handleCancel = async () => {
            if (onCancel) {
                try {
                    await onCancel();
                } catch (e) {
                    console.error("onCancel 콜백 실행 중 오류 발생:", e);
                }
            }
            destroyCurrentDialog();
        };

        const handleConfirm = async () => {
            if (isLoading.value) return; // 중복 클릭 방지
            isLoading.value = true;
            try {
                await onConfirm(); // 사용자가 제공한 비동기 확인 로직 실행
            } catch (error) {
                console.error("onConfirm 콜백 실행 중 오류 발생:", error);
                // 오류 발생 시 다이얼로그를 닫을지 여부는 여기서 결정하거나,
                // onConfirm 콜백 내에서 오류를 처리하도록 유도할 수 있습니다.
                // 현재는 finally 블록에서 항상 다이얼로그를 닫습니다.
            } finally {
                isLoading.value = false;
                destroyCurrentDialog();
            }
        };

        const renderContent = typeof content === "function" ? content : () => content;

        dialogInstance = dialogApi.create({
            title,
            content: renderContent,
            showIcon,
            maskClosable,
            closable,
            // 사용자 정의 액션 버튼을 사용하므로 Naive UI 기본 버튼은 비활성화
            positiveText: undefined,
            negativeText: undefined,
            action: () =>
                h("div", { style: "display: flex; justify-content: flex-end; gap: 8px;" }, [
                    h(NButton, { ...negativeButtonProps, onClick: handleCancel }, () => negativeText),
                    h(NButton, { ...positiveButtonProps, loading: isLoading.value, onClick: handleConfirm }, () =>
                        isLoading.value ? confirmButtonLoadingText : positiveText
                    ),
                ]),
        });
    };

    return {
        show,
    };
}
